/** Effet de pression des boutons (demande d'Alexis du 01/10/2026 : « dès que l'utilisateur tape sur un bouton, je
 *  veux un effet zoom in / out ») : le contrôle se resserre sous le doigt ou la souris, puis reprend sa taille au
 *  relâcher, avec un léger rebond. Un seul écouteur pour toute l'application, au lieu d'une classe par bouton ; il
 *  marche aussi au doigt sur l'iPad, où Safari n'applique pas `:active`. Animation Web de la propriété `scale`,
 *  qui se compose avec les `translate` et `rotate` des classes sans toucher à leurs transitions. Plus le contrôle
 *  est grand, plus l'effet est discret (une carte de dossier ne bouge que de quelques pixels). Mouvement réduit
 *  demandé par le poste : aucun effet. `data-no-press` exclut un contrôle (poignée de glissé, par exemple).
 *
 *  Cases à cocher comprises (R12) : case de la charte (`role="checkbox"`), case native et bouton radio natifs, et
 *  l'étiquette qui les enveloppe directement (la ligne entière se resserre). Les autres étiquettes en sont exclues :
 *  `Field` enveloppe des champs texte, où un appui ne doit rien animer. */

const BASE = [
  "button",
  "a[href]",
  "summary",
  '[role="button"]',
  '[role="tab"]',
  '[role="radio"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
];
const WRAPPING_LABELS = ['label:has(> input[type="checkbox"])', 'label:has(> input[type="radio"])'];

/** Sélecteur des contrôles pressables ; sans `:has` (navigateur ancien), les étiquettes en sont retirées au lieu
 *  de faire échouer `closest`. */
const PRESSABLE = (() => {
  let hasSupported = false;
  try {
    hasSupported = typeof CSS !== "undefined" && CSS.supports("selector(:has(> a))");
  } catch {
    hasSupported = false;
  }
  return [...BASE, ...(hasSupported ? WRAPPING_LABELS : [])].join(", ");
})();

const pressed = new Map<HTMLElement, { animation: Animation; depth: number }>();

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Contrôle pressable sous l'élément touché ; null s'il est désactivé ou exclu. */
function pressable(target: EventTarget | null): HTMLElement | null {
  const element = target instanceof Element ? target.closest<HTMLElement>(PRESSABLE) : null;
  if (!element || element.closest("[data-no-press]")) return null;
  if (element.matches(":disabled") || element.getAttribute("aria-disabled") === "true") return null;
  // Étiquette d'une case native désactivée : pas d'effet non plus.
  if (element.tagName === "LABEL" && element.querySelector(":scope > input:disabled")) return null;
  return element;
}

/** Resserrement : 6 % pour un bouton, jusqu'à 1,5 % pour un grand élément (environ 4 px de chaque côté). */
function depthOf(element: HTMLElement): number {
  const { width, height } = element.getBoundingClientRect();
  return Math.min(0.06, Math.max(0.015, 8 / Math.max(width, height, 1)));
}

function press(element: HTMLElement): void {
  if (typeof element.animate !== "function" || reducedMotion()) return;
  release(element, false);
  const depth = depthOf(element);
  const animation = element.animate([{ scale: "1" }, { scale: String(1 - depth) }], {
    duration: 90,
    easing: "ease-out",
    fill: "forwards",
  });
  pressed.set(element, { animation, depth });
}

function release(element: HTMLElement, bounce = true): void {
  const state = pressed.get(element);
  if (!state) return;
  pressed.delete(element);
  if (bounce) {
    const from = String(1 - state.depth);
    element.animate([{ scale: from }, { scale: String(1 + state.depth * 0.3), offset: 0.55 }, { scale: "1" }], {
      duration: 240,
      easing: "ease-out",
    });
  }
  state.animation.cancel();
}

function releaseAll(): void {
  for (const element of [...pressed.keys()]) release(element);
}

let installed = false;

/** Installe l'effet pour toute l'application (une fois). */
export function installPressFeedback(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  const options = { capture: true, passive: true } as const;
  document.addEventListener(
    "pointerdown",
    (event) => {
      if (event.button !== 0) return;
      const element = pressable(event.target);
      if (element) press(element);
    },
    options,
  );
  document.addEventListener("pointerup", releaseAll, options);
  document.addEventListener("pointercancel", releaseAll, options);
  document.addEventListener("dragstart", releaseAll, options);
  // Clavier : Entrée ou Espace sur un contrôle mis au point, même effet qu'au doigt.
  document.addEventListener(
    "keydown",
    (event) => {
      if ((event.key === "Enter" || event.key === " ") && !event.repeat) {
        const element = pressable(event.target);
        if (element && element === document.activeElement) press(element);
      }
    },
    options,
  );
  document.addEventListener("keyup", releaseAll, options);
  window.addEventListener("blur", releaseAll);
}
